import { DatabaseListResponse, DatabaseResponse } from '@couchbasecloud/base-ui/types/database-service.types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { hoursToMilliseconds } from 'date-fns';
import { useNotifications } from 'hooks/use-notifications/use-notifications';
import { useStorage } from 'hooks/use-storage/use-storage';
import { useUrlManager } from 'hooks/use-url-manager/use-url-manager';
import { useUser } from 'hooks/use-user/use-user';
import { getAccessibleDatabases, listDatabases } from 'sync/database-service';
import { QueryOptions } from 'sync/query';
import { SearchResult } from 'types/store';
import { checkAndMaybeNotifyAboutCancelledMaintenance } from 'utils/maintenance';
import { ReactQueryKeys } from 'utils/react-query';
import { getDbPrefix, getEntireDatabaseList, includeKeySelectorResults, includeSearchSuggestions } from './use-database-list.utils';

type UseDatabaseListProps = {
  organizationId: string;
  refetchInterval?: number | false;
  enabled?: boolean;
  fetchAccessibleDatabase?: boolean;
  onSuccess?: (data: DatabaseListResponse) => void;
} & (
  | {
      page?: QueryOptions['page'];
      perPage?: QueryOptions['perPage'];
      sortBy?: QueryOptions['sortBy'];
      sortDirection?: QueryOptions['sortDirection'];
    }
  | {
      entire: true;
    }
);

export const DEFAULT_PAGE = 1;
export const DEFAULT_PER_PAGE = 100;
export const DEFAULT_SORT_BY = 'name';
export const DEFAULT_SORT_ORDER = 'desc';
const DATABASE_LIST_CACHE_TIME = hoursToMilliseconds(10);

export const useDatabaseList = ({
  organizationId,
  fetchAccessibleDatabase = false,
  refetchInterval = false,
  enabled = true,
  onSuccess,
  ...props
}: UseDatabaseListProps) => {
  const { createURL } = useUrlManager();
  const queryClient = useQueryClient();

  /**
   * Keep track of notifications that we have already shown to the user.
   */
  const { read: readMaintenanceCancelledClusters, save: saveMaintenanceCancelledClusters } = useStorage<
    Record<string, { lastCancelledAt: string; lastCancelledBy: string; hasBeenDismissed: boolean }>
  >({
    storageKey: 'cb:cancelledMaintenanceNotifications',
    fallbackValue: {},
  });

  const { user } = useUser();
  const { createNotification } = useNotifications();

  const page = 'page' in props ? props.page || DEFAULT_PAGE : DEFAULT_PAGE;
  const perPage = 'perPage' in props ? props.perPage || DEFAULT_PER_PAGE : DEFAULT_PER_PAGE;
  const entire = 'entire' in props ? props.entire : false;
  const sortBy = 'sortBy' in props ? props.sortBy : DEFAULT_SORT_BY;
  const sortDirection = 'sortDirection' in props ? props.sortDirection : DEFAULT_SORT_ORDER;

  const queryKey = ReactQueryKeys.DATABASE_LIST(organizationId, page, perPage, entire, sortBy, sortDirection);

  const query = useQuery(
    queryKey,
    () => {
      if (entire) {
        return getEntireDatabaseList({ organizationId, startPage: page, perPage, sortBy, sortDirection });
      }

      return listDatabases({ organizationId }, { page, perPage, sortBy, sortDirection });
    },
    {
      cacheTime: DATABASE_LIST_CACHE_TIME,
      refetchInterval,
      enabled: !!organizationId && enabled,
      onSuccess: (data: DatabaseListResponse) => {
        if (onSuccess) {
          onSuccess(data);
        }

        checkAndMaybeNotifyAboutCancelledMaintenance({
          userId: user?.id,
          entityName: 'cluster',
          entitiesToCheck: data.data.flatMap((db) => db.data),
          previousCancelledInfo: readMaintenanceCancelledClusters,
          createNotification,
          saveLastCancelledInfo: saveMaintenanceCancelledClusters,
        });
      },
    }
  );

  const databaseList = query.data?.data.filter((record) => record.data) || [];
  const accessibleDatabasesQuery = useQuery(
    ReactQueryKeys.ACCESSIBLE_DATABASES(organizationId),
    () => getAccessibleDatabases({ organizationId }),
    {
      enabled: !!organizationId && enabled && fetchAccessibleDatabase,
      refetchInterval: false,
      cacheTime: DATABASE_LIST_CACHE_TIME,
    }
  );

  // You can now access the list of accessible databases
  const accessibleDatabases = accessibleDatabasesQuery.data || [];

  const search = (term: string, maxResults?: number): SearchResult[] => {
    if (!term || databaseList.length === 0) {
      return [];
    }

    const results: SearchResult[] = [];
    const searchTerm = term.toLowerCase();
    const set = new Set<string>();

    // addUniqueResult appends a result within our closure if it is not already
    // present in the list.
    const addUniqueResult = (record: DatabaseResponse, rank: number) => {
      // Quit if we have reached a desired maximum result size.
      if (maxResults && set.size >= maxResults) {
        return;
      }

      // Because we can search multiple properties we can have duplicates.
      // Deduplicate by ID;
      const { id } = record.data;

      if (set.has(id)) {
        return;
      }

      const dbPrefix = getDbPrefix(record.data);

      results.push({
        href: createURL('/database/datatools/documents', {
          organizationId: record.data.tenantId,
          projectId: record.data.project.id,
          databaseId: record.data.id,
        }),
        icon: record.data.provider.deliveryMethod === 'serverless' ? 'database-serverless' : 'database',
        rank,
        resource: 'database',
        title: record.data.name,
        summary:
          record.data.description && record.data.description.trim().length > 0
            ? record.data.description
            : `${dbPrefix} in project ${record.data.project.name}`,
        isFocused: false,
      });

      set.add(id);
    };

    const isVersionRegEx = /^[0-9]*\.?[0-9]*$/;
    const VERSIONS = ['6', '7', '8', '9'];
    const isVersionSearch = VERSIONS.some((version) => searchTerm.startsWith(version)) ? !!searchTerm.match(isVersionRegEx) : false;

    includeSearchSuggestions({
      databaseList,
      searchTerm,
      isVersionSearch,
      addUniqueResult,
    });

    // If our set is populated we've found an exact match on an ID and we can
    // stop looking. There won't be another GUID like this one!
    if (set.size !== 0) {
      return results.sort((a, b) => {
        return a.title.localeCompare(b.title);
      });
    }

    // If this is a version search and we have no results we'll quit.
    if (isVersionSearch) {
      return results;
    }

    // Finally, we will iterate over the fields we wish to search and collect
    // matches.
    includeKeySelectorResults({
      databaseList,
      addUniqueResult,
      maxResults,
      searchTerm,
      resultSet: set,
    });

    // Return a sorted list of results with the highest rank first.
    return results.sort((a, b) => {
      return b.rank - a.rank;
    });
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  return {
    search,
    databaseList,
    query,
    invalidate,
    accessibleDatabases,
    entireListReady: query.data && query.data?.data.length === query.data?.cursor.pages.totalItems,
  };
};
